import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ListPageTopBar } from "@/components/layout/dashboard-layout";
import { useTable } from "@/hooks/useTable";
import { ListFooter } from "@/components/shared/list-footer";

import { ErrorBoundary } from "react-error-boundary";
import { RecordNotFound } from "@/components/shared/record-not-found";
import { TableColumnsHandle } from "@/components/shared/table-columns-handle";
import { Columns3Icon, FilterIcon, X } from "lucide-react";
import { useMutations } from "./utils/react-hook/use-mutations";
import { ReactTable, ReactTableSkelton } from "./utils";
import { Fragment } from "react/jsx-runtime";
import ErrorFallback from "@/components/shared/error-fallback";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { RangeDateSelector } from "@/components/form-input/range-date-selector";

export const EmailInbox = () => {
  const {
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,

    skeltonLoading,
    tableColumn,
    setTableColumn,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    dateFilter,
    onDateChange,
  } = useMutations();

  const {
    tableHeader,
    table,
    tableKeys,
    onResize,
    onSorting,
  } = useTable({
    data: listData,
    view: [
      "subject",
      "sender",
      "recipient_list",
      "provider",
      "folder",
      "status",
      "received_at",
      "listener_email",
      "attachment_list",
    ],
    verifyKey: "email_inbox",
    visible: tableColumn,
    avoid: [""],
  });

  return (
    <Fragment>
      <div className="flex flex-col gap-5">
        <ListPageTopBar title="Email Inbox">
          <RangeDateSelector onChange={onDateChange} date={dateFilter} />

          <TableColumnsHandle
            tableKeys={tableKeys}
            tableColumn={tableColumn}
            setTableColumn={setTableColumn}
            onSorting={onSorting}
            tableKey="email_inbox"
            labels={{
              subject: "Subject",
              sender: "Sender",
              recipient_list: "Recipients",
              provider: "Provider",
              folder: "Folder",
              status: "Status",
              received_at: "Received At",
              listener_email: "Listener Email",
              attachment_list: "Attachments",
            }}
          >
            <Columns3Icon /> Column
          </TableColumnsHandle>

          <ConditionRenderer condition={Object.keys(filter).length !== 0}>
            <>
              <Button variant={"action"} onClick={filterRequest}>
                <FilterIcon /> Filter
              </Button>
              <Button variant={"action"} onClick={clearFilter}>
                <X /> Clear Filter
              </Button>
            </>
          </ConditionRenderer>
        </ListPageTopBar>

        {skeltonLoading ? (
          <ReactTableSkelton />
        ) : (
          <Card className="overflow-hidden">
            <CardContent className="max-w-[100%] p-0 overflow-auto scrollbar h-auto min-h-[350px] md:h-[calc(100dvh-340px)]">
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                  <ErrorFallback
                    error={error}
                    errorInfo={error.stack || ""}
                    resetErrorBoundary={resetErrorBoundary}
                  />
                )}
              >
                <ReactTable
                  tableHeader={tableHeader}
                  onResize={onResize}
                  table={table}
                  permission={permission}
                  filter={filter}
                  setFilter={setFilter}
                  listData={listData}
                  filterRequest={filterRequest}
                />
                {table.length === 0 ? <RecordNotFound /> : null}
              </ErrorBoundary>
            </CardContent>
            <ErrorBoundary
              fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                  error={error}
                  errorInfo={error.stack || ""}
                  resetErrorBoundary={resetErrorBoundary}
                />
              )}
            >
              <hr />
              <ListFooter
                onPageChange={onPageChange}
                onPerPageChange={onPerPageChange}
                page={paginationData.page}
                perPage={paginationData.perPage}
                totalItem={paginationData.totalItem}
                className="px-5 py-5"
                isEmpty={table.length === 0}
              />
            </ErrorBoundary>
          </Card>
        )}
      </div>
    </Fragment>
  );
};
